import type { ComponentType, ReactElement } from 'react';
import { AlertCircle, type LucideProps } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { describeError, type ErrorResolver } from '@/lib/form/errorMessages';

interface ErrorStateProps {
  icon?: ComponentType<LucideProps>;
  title?: string;
  /** Overrides the explanation; prefer passing `error`, which says what went wrong. */
  description?: string;
  /**
   * The error that stopped the load. Its explanation becomes the description —
   * why it failed, not just that it did — and a request the server answered
   * shows its reference, the id its logs know it by.
   */
  error?: unknown;
  /** A feature's resolver for `error`; the shared baseline otherwise. */
  resolveKey?: ErrorResolver;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
}

export function ErrorState({
  icon: Icon = AlertCircle,
  title,
  description,
  error,
  resolveKey,
  onRetry,
  retryLabel,
  className,
}: ErrorStateProps): ReactElement {
  const { t } = useTranslation();
  const hasError = error !== undefined && error !== null;
  const explanation =
    description ??
    (hasError ? describeError(t, error, resolveKey) : t('common.errorState.description'));
  const reference =
    error instanceof ApiError && error.requestId
      ? t('errors.reference', { requestId: error.requestId })
      : null;
  return (
    <div
      role="alert"
      data-testid="error-state"
      className={cn('flex flex-col items-center justify-center gap-3 py-12 text-center', className)}
    >
      <div className="rounded-full bg-destructive/10 p-3 text-destructive">
        <Icon className="h-6 w-6" aria-hidden="true" />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">
          {title ?? t('common.errorState.title')}
        </p>
        <p className="max-w-prose text-sm text-muted-foreground">{explanation}</p>
        {reference ? <p className="text-xs text-muted-foreground/80">{reference}</p> : null}
      </div>
      {onRetry ? (
        <Button variant="outline" onClick={onRetry} className="mt-2 h-8 px-3 py-1 text-xs">
          {retryLabel ?? t('common.errorState.retry')}
        </Button>
      ) : null}
    </div>
  );
}
